/**
 * myPokemonBackup — client half of the My Pokémon cloud backup (2026-08-28).
 *
 * The retention play: the My Pokémon database is device-first, and before
 * this module a lost phone meant a lost collection. The Worker stores every
 * signed-in trainer's records (PUT /api/my-pokemon/backup, opaque JSON, LWW
 * on the device-clock updatedAt); Pro unlocks pulling the copy back down
 * (GET, 403 pro_required with a count otherwise) — the same Backup x Pro
 * policy the dex sync has run since b19.
 *
 * WHAT LIVES HERE
 *   - the on/off pref (Settings toggle; default ON),
 *   - the PUSH INDEX: the {recordId -> updatedAt} map of what this device
 *     last successfully pushed, which makes the delta computable in O(n)
 *     and, crucially, makes DELETIONS visible: an id in the index that is no
 *     longer in the store was deleted on this device, so a tombstone goes up.
 *     No store schema change needed — the diff carries the fact.
 *   - status meta for the Settings card (last backup time, counts),
 *   - the pure delta/merge helpers the MyPokemonSyncer drives.
 *
 * The syncer (components/MyPokemonSyncer.tsx) owns the network; everything
 * here is storage + pure computation so it unit-tests without a server.
 */
import { useSyncExternalStore } from "react";
import type { MyPokemonRecord } from "@/lib/myPokemonStore";

const ENABLED_KEY = "nexus.myPokemonBackupEnabled.v1";
const INDEX_KEY = "nexus.myPokemonBackupIndex.v1";
const META_KEY = "nexus.myPokemonBackupMeta.v1";
export const MY_POKEMON_BACKUP_EVENT = "nexus:my-pokemon-backup-changed";
/** Settings' "Back up now" broadcasts this; the mounted syncer pushes. */
export const MY_POKEMON_BACKUP_NOW_EVENT = "nexus:my-pokemon-backup-now";

/** Server batch caps (mirrors routes/myPokemonBackup.ts). */
export const BACKUP_PUSH_CHUNK = 100;
export const BACKUP_DELETE_CHUNK = 300;

/* ── On/off pref ─────────────────────────────────────────────────────────── */

/** Default ON: backup is a safety net, opting out is the deliberate act. */
export function isMyPokemonBackupEnabled(): boolean {
  try {
    if (typeof localStorage === "undefined") return true;
    return localStorage.getItem(ENABLED_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setMyPokemonBackupEnabled(enabled: boolean): void {
  try {
    if (enabled) localStorage.removeItem(ENABLED_KEY);
    else localStorage.setItem(ENABLED_KEY, "off");
  } catch {
    /* storage blocked */
  }
  emitBackupChanged();
}

/* ── Push index ──────────────────────────────────────────────────────────── */

/** recordId -> updatedAt (device clock) as last successfully pushed. */
export type BackupIndex = Record<string, number>;

export function readBackupIndex(): BackupIndex {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: BackupIndex = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function writeBackupIndex(index: BackupIndex): void {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch {
    /* storage blocked: the next delta re-pushes, which the LWW gate no-ops */
  }
}

/* ── Status meta (Settings card) ─────────────────────────────────────────── */

export interface BackupMeta {
  /** Device time of the last successful push or pull; null = never. */
  lastSyncAt: number | null;
  /** Records known backed up (index size after the last successful sync). */
  backedUpCount: number;
  /**
   * The server's non-deleted row count from a pro_required 403, so the
   * Settings card can tell a free trainer their N Pokémon are safe; null
   * when Pro (restore ran) or unknown.
   */
  proLockedCount: number | null;
}

const EMPTY_META: BackupMeta = { lastSyncAt: null, backedUpCount: 0, proLockedCount: null };

export function readBackupMeta(): BackupMeta {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return EMPTY_META;
    const p = JSON.parse(raw) as Partial<BackupMeta>;
    return {
      lastSyncAt: typeof p.lastSyncAt === "number" ? p.lastSyncAt : null,
      backedUpCount: typeof p.backedUpCount === "number" ? p.backedUpCount : 0,
      proLockedCount: typeof p.proLockedCount === "number" ? p.proLockedCount : null,
    };
  } catch {
    return EMPTY_META;
  }
}

export function writeBackupMeta(patch: Partial<BackupMeta>): void {
  const next = { ...readBackupMeta(), ...patch };
  try {
    localStorage.setItem(META_KEY, JSON.stringify(next));
  } catch {
    /* storage blocked */
  }
  emitBackupChanged();
}

function emitBackupChanged(): void {
  try {
    window.dispatchEvent(new Event(MY_POKEMON_BACKUP_EVENT));
  } catch {
    /* SSR */
  }
}

// Stable snapshot so useSyncExternalStore doesn't loop the renderer.
let statusSnapshot: { enabled: boolean; meta: BackupMeta } | null = null;

function statusSubscribe(cb: () => void): () => void {
  const handler = () => {
    statusSnapshot = null;
    cb();
  };
  window.addEventListener(MY_POKEMON_BACKUP_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(MY_POKEMON_BACKUP_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

function statusGetSnapshot() {
  if (!statusSnapshot) {
    statusSnapshot = { enabled: isMyPokemonBackupEnabled(), meta: readBackupMeta() };
  }
  return statusSnapshot;
}

/** Reactive backup status for the Settings card. */
export function useMyPokemonBackupStatus(): { enabled: boolean; meta: BackupMeta } {
  return useSyncExternalStore(statusSubscribe, statusGetSnapshot, statusGetSnapshot);
}

/* ── Pure delta / wire helpers ───────────────────────────────────────────── */

export interface BackupTombstone {
  id: string;
  deletedAt: number;
}

export interface BackupDelta {
  /** Records new or edited since the index was last written. */
  changed: MyPokemonRecord[];
  /** Ids the index knows that the store no longer holds: deleted here. */
  tombstones: BackupTombstone[];
}

/**
 * What this device still owes the server. `now` stamps the tombstones: the
 * exact deletion instant was not recorded, and a stamp taken at diff time is
 * always >= it, which is the direction LWW needs (the tombstone must beat
 * the record it deletes).
 */
export function computeBackupDelta(
  records: readonly MyPokemonRecord[],
  index: BackupIndex,
  now: number = Date.now(),
): BackupDelta {
  const changed: MyPokemonRecord[] = [];
  const live = new Set<string>();
  for (const r of records) {
    live.add(r.id);
    const pushedAt = index[r.id];
    if (pushedAt == null || r.updatedAt > pushedAt) changed.push(r);
  }
  const tombstones: BackupTombstone[] = [];
  for (const id of Object.keys(index)) {
    if (!live.has(id)) tombstones.push({ id, deletedAt: now });
  }
  return { changed, tombstones };
}

/** The index after a successful push of `delta` on top of `index`. */
export function indexAfterPush(index: BackupIndex, delta: BackupDelta): BackupIndex {
  const next: BackupIndex = { ...index };
  for (const r of delta.changed) next[r.id] = r.updatedAt;
  for (const t of delta.tombstones) delete next[t.id];
  return next;
}

/** One pulled wire row (GET /api/my-pokemon/backup). */
export interface PulledBackupRow {
  id: string;
  payload: Record<string, unknown> | null;
  /** DEVICE time of the row's last statement (the server echoes it back). */
  updatedAt: number;
  deleted: boolean;
}

/**
 * The index a fresh pull establishes: every live server row at its server
 *-known device time. Local-only records are deliberately NOT indexed, so the
 * next delta pushes them up; tombstoned ids are absent for the same reason
 * in reverse (nothing local should re-push them unless re-created, and a
 * re-created record enters as "not in index" = changed).
 */
export function indexFromPull(rows: readonly PulledBackupRow[]): BackupIndex {
  const out: BackupIndex = {};
  for (const r of rows) {
    if (!r.deleted) out[r.id] = r.updatedAt;
  }
  return out;
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Test seam. */
export function __resetMyPokemonBackupForTest(): void {
  statusSnapshot = null;
  try {
    localStorage.removeItem(ENABLED_KEY);
    localStorage.removeItem(INDEX_KEY);
    localStorage.removeItem(META_KEY);
  } catch {
    /* ignore */
  }
}
